import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToast } from './Toast';
import SubscriptionModal from './SubscriptionModal';
import { useProfile } from '../contexts/ProfileContext';
import StartMembershipNow from "./StartMembershipNow";
import { useAuth } from '../contexts/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';

const PaywallModal = ({
  isOpen, onClose, starCost, currentStars,
  contentType = 'content', apiClient, user
}) => {
  const navigate = useNavigate();
  const { requireParentalVerification } = useProfile();
  const { isAuthenticated } = useAuth();
  const { trackPaywallShown, trackPaywallCtaClicked } = useAnalytics();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useEffect(() => {
    if (isOpen) trackPaywallShown(contentType, starCost, currentStars);
  }, [isOpen, contentType, starCost, currentStars, trackPaywallShown]);

  if (!isOpen) return null;

  // Logged-out users can't subscribe or buy anything — prompt them to create an
  // account / log in first, and hide the plans + star-purchase UI.
  const isLoggedOut = !isAuthenticated;
  const contentTypeText = { character: 'character', story: 'story', content: 'content' }[contentType] || 'content';
  const isOutOfStars = contentType !== 'download' && contentType !== 'premium' && !contentType.startsWith('upgrade_');
  const showPurchaseSection = isOutOfStars && !isLoggedOut;

  const handleSubscribe = async () => {
    trackPaywallCtaClicked('subscribe', contentType);
    requireParentalVerification('subscribe to premium', async () => {
      if (!user?.id || !user?.email) { showToast("Unable to process. Try logging out and back in.", "error"); return; }
      if (isProcessing) return;
      try {
        setIsProcessing(true);
        showToast("Redirecting to checkout...", "info");
        const response = await apiClient.createStripeCheckoutSession(user.id, user.email);
        if (response.status && response.data.checkout_url) { window.location.href = response.data.checkout_url; }
        else { throw new Error(response.error || "Failed"); }
      } catch (error) { showToast("Failed to start checkout.", "error"); setIsProcessing(false); }
    });
  };

  const handleShop = () => {
    trackPaywallCtaClicked('shop', contentType);
    requireParentalVerification('access star shop', () => { onClose(); navigate('/settings?tab=shop'); });
  };

  const handleViewPlans = () => {
    trackPaywallCtaClicked('view_plans', contentType);
    requireParentalVerification('view subscription plans', () => { setShowSubscriptionModal(true); });
  };

  const handleLogin = () => {
    trackPaywallCtaClicked('login', contentType);
    onClose();
    navigate('/auth?mode=signup');
  };

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
  const starPackages = [
    { stars: 75, price: 4.99 },
    { stars: 175, price: 9.99 },
    { stars: 400, price: 19.99, popular: true },
    { stars: 1000, price: 49.99 },
  ];

  // Members-only model: only the sellable plans are advertised here.
  const plans = [
    { name: 'Pro', price: '$14.99', stars: '250/wk', popular: true },
    { name: 'Family', price: '$24.99', stars: '650/wk' },
  ];

  return (
    <>
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center p-4"
        style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          style={{
            background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 28,
            boxShadow: "0px 5px 0px rgba(11,26,51,0.12), 0px 24px 64px rgba(11,26,51,0.24)",
            width: "100%",
            maxWidth: showPurchaseSection ? 640 : 440,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            fontFamily: "'Inter', 'Fredoka', sans-serif",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: "absolute", top: 14, right: 14, zIndex: 3,
              width: 34, height: 34, borderRadius: 11,
              background: "#FFFFFF", color: "#5A6B85",
              border: "2px solid rgba(11,26,51,0.08)",
              boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Scrollable body */}
          <div style={{ padding: "28px 26px 16px", overflowY: "auto", flex: 1, minHeight: 0 }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <img
                src="/assets/pixley-panda-nobg.png"
                alt=""
                style={{ width: 72, height: 72, objectFit: "contain", margin: "0 auto", display: "block" }}
              />
            </div>

            <div style={{ textAlign: "center", marginBottom: 22 }}>
              {contentType === 'download' || contentType === 'premium' || contentType.startsWith('upgrade_') ? (
                <>
                  <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                    Premium
                  </span>
                  <h2 style={{ ...FREDOKA, fontSize: 28, fontWeight: 700, color: "#0B1A33", textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 6px", lineHeight: 1.05 }}>
                    Premium <span style={{ color: "#0065F8" }}>Feature</span>
                  </h2>
                  <p style={{ ...FREDOKA, fontSize: 14, color: "#5A6B85", margin: 0 }}>
                    {isLoggedOut
                      ? 'Log in or create a free account to unlock Premium downloads.'
                      : contentType === 'download'
                        ? 'Video downloads are available for Premium members.'
                        : 'This feature requires a Premium subscription.'}
                  </p>
                </>
              ) : (
                <>
                  <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                    Star Balance
                  </span>
                  <h2 style={{ ...FREDOKA, fontSize: 28, fontWeight: 700, color: "#0B1A33", textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 6px", lineHeight: 1.05 }}>
                    Not Enough <span style={{ color: "#0065F8" }}>Stars</span>
                  </h2>
                  <p style={{ ...FREDOKA, fontSize: 14, color: "#5A6B85", margin: 0 }}>
                    You need{" "}
                    <strong style={{ color: "#0B1A33" }}>{starCost}</strong> stars but have{" "}
                    <strong style={{ color: "#0B1A33" }}>{currentStars.toLocaleString()}</strong>.
                  </p>
                </>
              )}
            </div>

            {showPurchaseSection && (
              <>
                {/* Trialers: skip the wait — convert now, weekly stars land instantly */}
                <StartMembershipNow variant="banner" onConverted={onClose} />
                <div style={{ textAlign: "center", marginBottom: 12 }}>
                  <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                    Buy Stars
                  </span>
                  <h3 style={{ ...FREDOKA, fontSize: 18, fontWeight: 700, color: "#0B1A33", textTransform: "uppercase", letterSpacing: "-0.01em", margin: "2px 0 0" }}>
                    Top up now
                  </h3>
                </div>

                {/* Star packages */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" style={{ marginBottom: 14, paddingTop: 12 }}>
                  {starPackages.map((pkg, idx) => (
                    <button
                      key={idx}
                      onClick={handleShop}
                      className="relative transition active:scale-[0.97]"
                      style={{
                        padding: "14px 10px 12px",
                        borderRadius: 18,
                        border: pkg.popular ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                        background: pkg.popular ? "linear-gradient(180deg, #EAF3FF 0%, #DBEAFE 100%)" : "#FFFFFF",
                        cursor: "pointer",
                        textAlign: "center",
                        boxShadow: pkg.popular
                          ? "0px 4px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.18)"
                          : "0px 3px 0px rgba(11,26,51,0.06)",
                      }}
                    >
                      {pkg.popular && (
                        <div
                          style={{
                            position: "absolute",
                            top: -10,
                            left: "50%",
                            transform: "translateX(-50%)",
                            ...FREDOKA,
                            background: "#0065F8",
                            color: "#FFFFFF",
                            fontWeight: 800,
                            fontSize: 9,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            padding: "4px 10px",
                            borderRadius: 999,
                            boxShadow: "0px 2px 0px #073B8E",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Popular
                        </div>
                      )}
                      <img
                        src="/assets/pixley_stars.png"
                        alt=""
                        style={{ width: 28, height: 28, margin: "0 auto 6px", display: "block" }}
                      />
                      <div style={{ ...FREDOKA, fontSize: 22, fontWeight: 800, color: "#0B1A33", lineHeight: 1 }}>{pkg.stars}</div>
                      <div style={{ ...FREDOKA, fontSize: 10, color: "#5A6B85", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginTop: 3, marginBottom: 8 }}>stars</div>
                      <div style={{ ...FREDOKA, fontSize: 16, fontWeight: 700, color: "#0065F8" }}>${pkg.price}</div>
                    </button>
                  ))}
                </div>
                <p style={{ ...FREDOKA, textAlign: "center", fontSize: 11, color: "#94A3B8", fontWeight: 600, margin: "0 0 20px" }}>One-time purchase</p>

                {/* OR divider */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "0 0 18px" }}>
                  <div style={{ flex: 1, height: 2, background: "rgba(11,26,51,0.06)", borderRadius: 999 }} />
                  <span style={{ ...FREDOKA, fontSize: 11, color: "#94A3B8", fontWeight: 800, letterSpacing: "0.16em" }}>OR</span>
                  <div style={{ flex: 1, height: 2, background: "rgba(11,26,51,0.06)", borderRadius: 999 }} />
                </div>

                <div style={{ textAlign: "center", marginBottom: 12 }}>
                  <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                    Subscribe
                  </span>
                  <h3 style={{ ...FREDOKA, fontSize: 18, fontWeight: 700, color: "#0B1A33", textTransform: "uppercase", letterSpacing: "-0.01em", margin: "2px 0 0" }}>
                    Weekly stars + more
                  </h3>
                </div>

                {/* Plans — uniform white cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" style={{ marginBottom: 8, paddingTop: 10 }}>
                  {plans.map((plan, idx) => (
                    <div
                      key={idx}
                      className="relative"
                      style={{
                        background: plan.popular ? "linear-gradient(180deg, #EAF3FF 0%, #DBEAFE 100%)" : "#FFFFFF",
                        border: plan.popular ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                        borderRadius: 16,
                        padding: "14px 8px 10px",
                        textAlign: "center",
                        boxShadow: plan.popular
                          ? "0px 3px 0px #073B8E"
                          : "0px 2px 0px rgba(11,26,51,0.06)",
                      }}
                    >
                      {plan.popular && (
                        <div
                          style={{
                            position: "absolute",
                            top: -9,
                            left: "50%",
                            transform: "translateX(-50%)",
                            ...FREDOKA,
                            background: "#0065F8",
                            color: "#FFFFFF",
                            fontWeight: 800,
                            fontSize: 9,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            padding: "3px 8px",
                            borderRadius: 999,
                            boxShadow: "0px 2px 0px #073B8E",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Popular
                        </div>
                      )}
                      <div style={{ ...FREDOKA, fontSize: 11, fontWeight: 800, color: "#0065F8", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 3 }}>{plan.name}</div>
                      <div style={{ ...FREDOKA, fontSize: 17, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>{plan.price}</div>
                      <div className="inline-flex items-center justify-center gap-1" style={{ ...FREDOKA, fontSize: 10, color: "#5A6B85", marginTop: 4, fontWeight: 600 }}>
                        {plan.stars.replace('/wk', '')}
                        <img src="/assets/pixley_stars.png" alt="" style={{ width: 10, height: 10 }} />
                        <span>/wk</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Sticky footer CTA */}
          <div
            style={{
              padding: "14px 22px 18px",
              background: "rgba(245,250,255,0.92)",
              borderTop: "2px solid rgba(11,26,51,0.06)",
              flexShrink: 0,
            }}
          >
            <button
              onClick={isLoggedOut ? handleLogin : handleViewPlans}
              className="w-full transition active:scale-[0.98] inline-flex items-center justify-center gap-2"
              style={{
                ...FREDOKA,
                padding: "13px 20px",
                background: "#0065F8",
                color: "#FFFFFF",
                border: "none",
                borderRadius: 999,
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
              }}
            >
              {isLoggedOut ? 'Log In or Sign Up' : 'View Plans & Subscribe'}
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {showSubscriptionModal && (
        <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => { setShowSubscriptionModal(false); onClose(); }} apiClient={apiClient} user={user} />
      )}
    </>
  );
};

export default PaywallModal;
